// International Workspace — Sport Workspaces v1.1 (4th sport-switcher sibling).
//
// Ported onto the basketball-style design system: scoped `.intl` CSS layer +
// _intlkit primitives (IntlHead, IntlHero, Scoreboard, Card, AwaitPanel,
// StatusPill, IIcon, …). Mirrors basketball.css / _bbkit.tsx so the two
// workspaces feel like siblings: kicker rail, hero with grain motif, Bebas-Neue
// scoreboard tiles, mono-eyebrow chips, dashed honest-empty panels.
//
// Sections: Dashboard / Signals / WC 2026 Hub / CLV / No-Bets.
// WC Hub has 5 sub-tabs: Fixtures / Teams / Futures / Bracket / Signals.
// Acca / Ladder / Tournaments deep-link into existing /intl-acca/* pages.
//
// HONEST BY CONSTRUCTION — every empty surface stays empty until its data
// source ships. Nothing fabricated, no fake group letters, no fake bracket.

import { Component, useState, useRef, useEffect, type JSX, type ReactNode, type CSSProperties, type MouseEvent as ReactMouseEvent } from "react";
import { Link } from "react-router-dom";
import "../../styles/intl-signals.css";
import {
  IntlHead, IntlHero, HeroChip, DefList, Scoreboard, Card,
  AwaitPanel, Loading, StatusPill, IIcon, NationFlag,
  fmtKick, fmtDateOnly, fmtOdds, daysUntil,
  sectionMeta, type IntlSection,
} from "./_intlkit.tsx";
import {
  useWcBoard,
  type WcFixture, type WcTeam, type WcFuture,
  type WcBracketFixture, type WcBracketRound, type WcSignal,
} from "./useWcBoard.ts";
import {
  useRoundsBoard, roundLabel,
  type RoundBucket, type RoundFixture, type RoundSignal, type RoundsModerationEntry,
} from "./useRoundsBoard.ts";
import { useBoardRoundsCard, type AgentStatus } from "./useBoardRoundsCard.ts";
import { useStandingTen, type StandingLeg } from "./useStandingTen.ts";
import { useCurrentInternationalSlate } from "./useCurrentInternationalSlate.ts";

export type { IntlSection };
const WC_KICKOFF = new Date("2026-06-11T00:00:00Z");

class IntelPanelBoundary extends Component<{ children: ReactNode }, { error: string | null }> {
  override state = { error: null as string | null };
  static getDerivedStateFromError(e: Error) { return { error: e.message }; }
  override render() {
    if (this.state.error) {
      return (
        <div className="rnd-intel">
          <div className="rnd-intel__section">
            <div className="rnd-intel__section-head">
              <span>Intel Panel Error</span>
            </div>
            <div className="rnd-intel__empty">
              Could not render intel panel: {this.state.error}
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Shell ──────────────────────────────────────────────────────────────
function Shell({ section, children }: { section: IntlSection; children: ReactNode }): JSX.Element {
  const h = sectionMeta[section];
  return (
    <div className="intl">
      <IntlHead
        kicker={h.kicker}
        title={h.title}
        status="International"
        live={false}
      />
      {children}
    </div>
  );
}

// ── Dashboard ──────────────────────────────────────────────────────────
interface PortalCard {
  title: string; body: string; to: string;
  status: "live" | "calibrating" | "dormant";
}
const PORTAL_CARDS: ReadonlyArray<PortalCard> = [
  { title: "International Acca", body: "Today's selection on the international slate, CLV-aware leg pricing.", to: "/intl-acca/hub", status: "live" },
  { title: "LADDER-Intl", body: "Tournament-cycle ladder built on international fixtures.", to: "/intl-acca/ladder", status: "live" },
  { title: "Tournaments", body: "Calendar of upcoming international windows + tournaments.", to: "/intl-acca/calendar", status: "live" },
  { title: "WC 2026 Archive", body: "World Cup 2026 fixtures, bracket and tournament history.", to: "/exchange/international/wc-2026", status: "dormant" },
  { title: "Signals", body: "Live edges across the international slate. Calibrating until the substrate signals wire in.", to: "/exchange/international/signals", status: "calibrating" },
  { title: "Tournament Pass", body: "Single-pass tournament unlock (UCL, UEL, WC).", to: "/intl-acca/pass", status: "live" },
  { title: "Slip Archive", body: "Every settled international slip, grouped by tournament.", to: "/intl-acca/archive", status: "live" },
  { title: "Debrief", body: "Post-tournament autopsy: what hit, what missed, what we learned.", to: "/intl-acca/debrief", status: "live" },
  { title: "CLV Track", body: "Pinnacle vs Stake CLV accrual on international picks.", to: "/exchange/international/clv", status: "calibrating" },
  { title: "No-Bet Report", body: "Honest log of intl matches we declined.", to: "/exchange/international/no-bets", status: "calibrating" },
];

function DashboardSection(): JSX.Element {
  const slate = useCurrentInternationalSlate();
  return (
    <>
      <IntlHero
        kicker={<>Workspace · {slate.tournament ?? "International football"}</>}
        title={<>International football</>}
        lede="Current national-team fixtures, selections and tournament history in one workspace."
        chips={
          <>
            <HeroChip tone="accent">{slate.tournament ?? "No active tournament"}</HeroChip>
            <HeroChip>International Acca</HeroChip>
            <HeroChip>Tournament archive</HeroChip>
          </>
        }
        aside={
          <DefList rows={[
            { k: "Tournament", v: slate.tournament ?? (slate.loading ? "Loading…" : "No active window") },
            { k: "Next kickoff", v: slate.nextKickoff ? fmtKick(slate.nextKickoff) : "—" },
            { k: "Fixtures", v: slate.loading ? "Loading…" : `${slate.fixtures} in 7 days` },
            { k: "With odds", v: slate.loading ? "Loading…" : `${slate.priced} / ${slate.fixtures}` },
          ]} />
        }
      />

      <Scoreboard tiles={[
        { label: "Upcoming fixtures", value: slate.loading ? "—" : slate.fixtures, sub: "Next seven days" },
        { label: "Fixtures with odds", value: slate.loading ? "—" : slate.priced, sub: "Current tournament" },
        { label: "Next kickoff", value: slate.nextKickoff ? fmtKick(slate.nextKickoff) : "—", sub: slate.tournament ?? "No active window" },
      ]} />

      <Card kicker="Surfaces" title="Workspace portal">
        <div className="intl-portal">
          {PORTAL_CARDS.map((c) => (
            <Link key={c.to} to={c.to} className="intl-portal-card">
              <div className="intl-portal-card__head">
                <span className="intl-portal-card__title">{c.title}</span>
                <StatusPill tone={c.status}>{c.status}</StatusPill>
              </div>
              <p className="intl-portal-card__body">{c.body}</p>
              <span className="intl-portal-card__cta">Open <IIcon name="chev" size={11} /></span>
            </Link>
          ))}
        </div>
      </Card>
    </>
  );
}

// ── Generic honest-empty using kit primitive ───────────────────────────
function EmptyHere({ title, body, action }: { title: string; body: string; action?: ReactNode }): JSX.Element {
  return (
    <>
      <AwaitPanel title={title} body={body} />
      {action ? <div style={{ marginTop: 16 }}>{action}</div> : null}
    </>
  );
}

// ── WC Hub: shared helpers ─────────────────────────────────────────────
function WcFixtureRow({ fx }: { fx: WcFixture }): JSX.Element {
  return (
    <div className="intl-fix">
      <div className="intl-fix__time">{fmtKick(fx.kickoff_at)}</div>
      <div className="intl-fix__teams">
        <span className="intl-fix__team">{fx.home_team}</span>
        <span className="intl-fix__vs">vs</span>
        <span className="intl-fix__team">{fx.away_team}</span>
      </div>
      <div className="intl-fix__odds" aria-label="Reference 1X2 odds">
        <span className="intl-fix__odds-cell"><em>1</em> {fmtOdds(fx.odds?.home)}</span>
        <span className="intl-fix__odds-cell"><em>X</em> {fmtOdds(fx.odds?.draw)}</span>
        <span className="intl-fix__odds-cell"><em>2</em> {fmtOdds(fx.odds?.away)}</span>
      </div>
    </div>
  );
}

// ── WC tabs ────────────────────────────────────────────────────────────
type WcTab = "fixtures" | "teams" | "futures" | "bracket" | "signals";
const WC_TABS: ReadonlyArray<{ key: WcTab; label: string }> = [
  { key: "fixtures", label: "Fixtures" },
  { key: "teams",    label: "Teams" },
  { key: "futures",  label: "Futures" },
  { key: "bracket",  label: "Bracket" },
  { key: "signals",  label: "Signals" },
];

function WcFixturesSection({ days }: { days: { date: string; fixtures: WcFixture[] }[] }): JSX.Element {
  return (
    <>
      {days.map((d) => (
        <section key={d.date} className="intl-day">
          <div className="intl-day-head">
            <span>{fmtDateOnly(d.date)}</span>
            <span>{d.fixtures.length} fixture{d.fixtures.length === 1 ? "" : "s"}</span>
          </div>
          {d.fixtures.map((fx) => <WcFixtureRow key={fx.fixture_id} fx={fx} />)}
        </section>
      ))}
    </>
  );
}

function WcTeamsSection({ teams }: { teams: WcTeam[] }): JSX.Element {
  if (!teams.length) {
    return <AwaitPanel icon="globe" title="No teams yet" body="Teams derive from persisted WC fixtures. Empty until the Odds-API sweep populates the schedule." />;
  }
  return (
    <>
      <p className="intl-note">
        {teams.length} teams · derived from real WC fixtures we've ingested. W-D-L columns fill automatically as match scores arrive — nothing shown before it's earned.
      </p>
      <div className="intl-teams-grid">
        {teams.map((t) => (
          <div key={t.team} className="intl-team-card">
            <div className="intl-team-card__name">{t.team}</div>
            <div className="intl-team-card__meta">{t.fixtures} fixture{t.fixtures === 1 ? "" : "s"}</div>
            <ul className="intl-team-card__fixtures">
              {t.fixture_list.map((f) => (
                <li key={f.fixture_id}>
                  <span className="intl-team-card__side">{f.side === "H" ? "vs" : "@"}</span>
                  {f.opponent}
                  <span className="intl-team-card__time">{fmtKick(f.kickoff_at)}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </>
  );
}

function WcFuturesSection({ futures }: { futures: WcFuture[] }): JSX.Element {
  if (!futures.length) {
    return (
      <AwaitPanel
        icon="trophy"
        title="No WC 2026 winner futures yet"
        body="Outright winner odds appear here when the Odds-API futures sweep runs against soccer_fifa_world_cup_winner. Not fabricated before then."
      />
    );
  }
  return (
    <>
      <p className="intl-note">Outright winner odds, shortest price first. Reference only.</p>
      <table className="intl-table">
        <thead><tr><th>Team</th><th className="r">Odds</th><th className="r">Implied</th></tr></thead>
        <tbody>
          {futures.map((f) => (
            <tr key={f.outright}>
              <td>{f.outright}</td>
              <td className="r">{f.odds.toFixed(2)}</td>
              <td className="r">{(100 / f.odds).toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

const ROUND_ORDER: WcBracketRound[] = ["R32", "R16", "QF", "SF", "3rd", "F"];
const ROUND_LABEL: Record<WcBracketRound, string> = {
  R32: "Round of 32",  R16: "Round of 16", QF: "Quarter-finals",
  SF: "Semi-finals",   "3rd": "Third-place playoff", F: "Final",
};

function WcBracketSection({ bracket }: { bracket: Partial<Record<WcBracketRound, WcBracketFixture[]>> }): JSX.Element {
  const present = ROUND_ORDER.filter((r) => bracket[r]?.length);
  if (!present.length) {
    return (
      <AwaitPanel
        icon="bracket"
        title="Bracket fills after group stage"
        body="The knockout draw is determined by group-stage outcomes (Jun 11 → Jun 27). We don't fabricate matchups before they're set. The moment KO fixtures land in the feed this surface lights up, round by round."
      />
    );
  }
  return (
    <div className="intl-bracket">
      {present.map((r) => (
        <section key={r} className="intl-bracket-round">
          <div className="intl-bracket-round-head">
            <span>{ROUND_LABEL[r]}</span>
            <span className="intl-bracket-count">{bracket[r]!.length}</span>
          </div>
          {bracket[r]!.map((fx) => (
            <WcFixtureRow key={fx.fixture_id} fx={{ ...fx, status: "scheduled" }} />
          ))}
        </section>
      ))}
    </div>
  );
}

function WcSignalsSection({ signals }: { signals: WcSignal[] }): JSX.Element {
  if (!signals.length) {
    return (
      <AwaitPanel
        icon="target"
        title="No WC signals yet"
        body="Live edges appear here once the international substrate emits priced signals on WC fixtures. Sorted by CLV gap, conviction tier first. Nothing fabricated — no signal = no row."
      />
    );
  }
  return (
    <>
      <p className="intl-note">{signals.length} live edge(s) on WC fixtures · sorted by CLV gap.</p>
      <table className="intl-table">
        <thead><tr>
          <th>Fixture</th><th>Market</th><th className="r">Fair</th><th className="r">Market</th>
          <th className="r">CLV</th><th>Tier</th>
        </tr></thead>
        <tbody>
          {signals.map((s, i) => (
            <tr key={`${s.fixture_id}-${i}`}>
              <td>{s.home_team} vs {s.away_team}<br/><small>{fmtKick(s.kickoff_at)}</small></td>
              <td>{s.market} · <strong>{s.selection}</strong></td>
              <td className="r">{fmtOdds(s.fair_odds)}</td>
              <td className="r">{fmtOdds(s.market_odds)}</td>
              <td className="r">{s.clv_gap != null ? `${(s.clv_gap * 100).toFixed(1)}%` : "—"}</td>
              <td>{s.conviction_tier ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function WcHubSection(): JSX.Element {
  const { board, loading, error } = useWcBoard();
  const [tab, setTab] = useState<WcTab>("fixtures");
  const wcDays = daysUntil(WC_KICKOFF);

  if (loading) return <Loading label="Loading WC board…" />;
  if (error) return (
    <AwaitPanel icon="info" title="Couldn't load WC 2026 board" body={error} />
  );
  if (!board || board.total_fixtures === 0) {
    return (
      <AwaitPanel
        icon="trophy"
        title="FIFA World Cup 2026 archive"
        body="No World Cup fixtures are available in this archive."
      />
    );
  }

  return (
    <>
      <div className="intl-wc-stats">
        <span><strong>{board.total_fixtures}</strong> fixtures</span><span className="sep">·</span>
        <span><strong>{board.fixtures_with_odds}</strong> w/ reference odds</span><span className="sep">·</span>
        <span><strong>{board.days.length}</strong> match days</span>
        {wcDays > 0 ? <><span className="sep">·</span><span>T-{wcDays}d to kickoff</span></> : null}
      </div>

      <nav className="intl-tabs" role="tablist" aria-label="WC sections">
        {WC_TABS.map((t) => {
          const count =
            t.key === "fixtures" ? board.total_fixtures :
            t.key === "teams"    ? (board.teams?.length ?? 0) :
            t.key === "futures"  ? (board.futures?.length ?? 0) :
            t.key === "bracket"  ? Object.values(board.bracket ?? {}).reduce((a, r) => a + (r?.length ?? 0), 0) :
            t.key === "signals"  ? (board.signals?.length ?? 0) : 0;
          return (
            <button
              key={t.key} type="button" role="tab"
              aria-selected={tab === t.key}
              className={`intl-tab${tab === t.key ? " active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}<span className="intl-tab__count">{count}</span>
            </button>
          );
        })}
      </nav>

      {tab === "fixtures" && <WcFixturesSection days={board.days} />}
      {tab === "teams"    && <WcTeamsSection    teams={board.teams ?? []} />}
      {tab === "futures"  && <WcFuturesSection  futures={board.futures ?? []} />}
      {tab === "bracket"  && <WcBracketSection  bracket={board.bracket ?? {}} />}
      {tab === "signals"  && <WcSignalsSection  signals={board.signals ?? []} />}
    </>
  );
}

// ── Rounds product ─────────────────────────────────────────────────────
// Per-round predictions surface. Predictions = substrate edges (real fair vs
// market prices, never guessed); moderator verdict = slip_moderation_log
// entry that touched fixtures in the round (closed-enum reason_code only —
// methodology stays in the audit table). Honest-empty until each data
// source fires.

function AgentPipeline({ agents }: { agents: AgentStatus[] }): JSX.Element | null {
  if (agents.length === 0) return null;
  const layers = [
    { n: 1, label: "Layer 1 · Intelligence", ids: ["BR-A01","BR-A02","BR-A03","BR-A04","BR-A05","BR-A06","BR-A07","BR-A08"] },
    { n: 2, label: "Layer 2 · Synthesis",     ids: ["BR-A09","BR-A10","BR-A11","BR-A12","BR-A13","BR-A14","BR-A15"] },
    { n: 3, label: "Layer 3 · Output",        ids: ["BR-A16","BR-A17","BR-A18","BR-A19","BR-A20"] },
  ];
  const byId = new Map(agents.map((a) => [a.agent_id, a]));
  return (
    <div className="rnd-intel__section">
      <div className="rnd-intel__section-head">
        <IIcon name="globe" size={13} />
        <span>Agent Pipeline</span>
        <span className="rnd-intel__head-count">{agents.filter((a) => a.status === "COMPLETED").length}/{agents.length}</span>
      </div>
      <div className="rnd-intel__pipeline">
        {layers.map((l) => (
          <div key={l.n} className="rnd-intel__layer">
            <span className="rnd-intel__layer-label">{l.label}</span>
            <div className="rnd-intel__agent-row">
              {l.ids.map((id) => {
                const a = byId.get(id);
                const st = a?.status ?? "PENDING";
                return (
                  <span key={id} className={`rnd-intel__agent-dot rnd-intel__agent-dot--${st.toLowerCase()}`} title={st}>
                    {id.replace("BR-A", "")}
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FixtureIntelPanel({ fx, legs = [] }: { fx: RoundFixture; legs?: DedupedLeg[] }): JSX.Element {
  const { data, loading } = useBoardRoundsCard(fx.fixture_id);
  const card = data?.card ?? null;
  const agents = data?.agents ?? [];
  const hasOdds = fx.odds && (fx.odds.home || fx.odds.draw || fx.odds.away);

  const fmtProb = (p: number | null | undefined): string =>
    typeof p === "number" && Number.isFinite(p) ? `${(p * 100).toFixed(1)}%` : "—";

  return (
    <div className="rnd-intel">
      {loading && <div className="rnd-intel__loading">Loading intel…</div>}

      {/* Prediction Card header (when agent data exists) */}
      {card?.strikza_call && (
        <div className="rnd-intel__call">
          <div className="rnd-intel__call-main">
            <span className="rnd-intel__call-label">STRIKZA Call</span>
            <span className="rnd-intel__call-value">{card.strikza_call}</span>
            {card.confidence_tier && (
              <span className={`rnd-intel__tier rnd-intel__tier--${card.confidence_tier.toLowerCase()}`}>
                Tier {card.confidence_tier}
              </span>
            )}
          </div>
          {card.intelligence_note && (
            <p className="rnd-intel__note">{card.intelligence_note}</p>
          )}
          {card.ev_delta != null && (
            <div className="rnd-intel__ev-row">
              <span>Fair {fmtOdds(card.ifpe_fair_price)}</span>
              <span>Book {fmtOdds(card.bookmaker_price)}</span>
              <span className={card.ev_delta > 0 ? "rnd-intel__ev--pos" : ""}>
                EV {card.ev_delta > 0 ? "+" : ""}{(card.ev_delta * 100).toFixed(1)}%
              </span>
              {card.clv_gate && (
                <StatusPill tone={card.clv_gate === "PASS" ? "live" : card.clv_gate === "FAIL" ? "dormant" : "calibrating"}>
                  {card.clv_gate.replace("_", " ")}
                </StatusPill>
              )}
            </div>
          )}
        </div>
      )}

      {/* Also Priced — top 2 alternative markets from agent pipeline */}
      {(() => {
        const CONV_COLORS: Record<string, string> = {
          STRONG: "var(--clv-pass, #4ade80)",
          MODERATE: "var(--clv-cond, #facc15)",
          SPECULATIVE: "var(--text-tertiary)",
          PROBABILITY_ONLY: "var(--text-tertiary)",
        };
        const picks = card?.secondary_picks?.length
          ? card.secondary_picks
          : null;
        if (!picks || picks.length === 0) return null;
        return (
          <div className="rnd-intel__section">
            <div className="rnd-intel__section-head">
              <IIcon name="chart" size={13} />
              <span>Also Priced</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {picks.map((p) => (
                <div key={p.code} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "8px 12px", borderRadius: 6,
                  background: "var(--surface-raised, rgba(255,255,255,0.03))",
                }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: "var(--sz-body-sm)", color: "var(--text)" }}>{p.label}</span>
                      <span className="ia-mono" style={{
                        fontSize: 9, padding: "1px 5px", borderRadius: 3,
                        background: "rgba(255,255,255,0.06)",
                        color: CONV_COLORS[p.conviction] ?? "var(--text-tertiary)",
                        textTransform: "uppercase", letterSpacing: "0.05em",
                      }}>
                        {p.conviction === "PROBABILITY_ONLY" ? "FAIR PRICE" : p.conviction}
                      </span>
                    </div>
                    <span style={{ fontSize: 10, color: "var(--text-tertiary)", lineHeight: 1.3 }}>
                      {p.reasoning}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 14, alignItems: "center", flexShrink: 0 }}>
                    <span className="ia-mono" style={{ fontSize: 11, color: "var(--accent-text)" }}>
                      {(p.prob * 100).toFixed(1)}%
                    </span>
                    <span className="ia-mono" style={{ fontSize: 11, color: "var(--text-secondary)" }}>
                      Fair {p.fair_odds.toFixed(2)}
                    </span>
                    {p.book_odds && p.book_odds > 1 && (
                      <span className="ia-mono" style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                        Book {p.book_odds.toFixed(2)}
                      </span>
                    )}
                    {p.ev > 0 && (
                      <span className="ia-mono" style={{
                        fontSize: 11,
                        color: p.ev > 0.03 ? "var(--clv-pass, #4ade80)" : "var(--clv-cond, #facc15)",
                      }}>
                        +{(p.ev * 100).toFixed(1)}%
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* IFPE Ensemble */}
      <div className="rnd-intel__section">
        <div className="rnd-intel__section-head">
          <IIcon name="target" size={13} />
          <span>Fair Price Engine</span>
        </div>
        <div className="rnd-intel__grid">
          <div className="rnd-intel__module">
            <span className="rnd-intel__mod-label">M1 · FIFA Ranking</span>
            <span className={`rnd-intel__mod-val${!card?.national_form_tfs ? " rnd-intel__mod-val--empty" : ""}`}>
              {card?.national_form_tfs != null ? `TFS ${card.national_form_tfs.toFixed(0)}` : "Not yet analysed"}
            </span>
          </div>
          <div className="rnd-intel__module">
            <span className="rnd-intel__mod-label">M2 · Tournament H2H</span>
            <span className={`rnd-intel__mod-val${!card?.h2h_dominance ? " rnd-intel__mod-val--empty" : ""}`}>
              {card?.h2h_dominance ?? "Not yet analysed"}
            </span>
          </div>
          <div className="rnd-intel__module">
            <span className="rnd-intel__mod-label">M3 · Squad Quality</span>
            <span className={`rnd-intel__mod-val${!card?.squad_confidence ? " rnd-intel__mod-val--empty" : ""}`}>
              {card?.squad_confidence?.replace("_", " ") ?? "Not yet analysed"}
            </span>
          </div>
          <div className="rnd-intel__module">
            <span className="rnd-intel__mod-label">M4 · Stage Factor</span>
            <span className={`rnd-intel__mod-val${!card?.env_advantage_score ? " rnd-intel__mod-val--empty" : ""}`}>
              {card?.env_advantage_score != null ? `${card.env_advantage_score.toFixed(1)} / 10` : "Not yet analysed"}
            </span>
          </div>
          <div className="rnd-intel__module">
            <span className={`rnd-intel__mod-val${!hasOdds && !card?.opening_line ? " rnd-intel__mod-val--empty" : ""}`}>
              <span className="rnd-intel__mod-label">M5 · Market Anchor</span>
              {hasOdds
                ? `${fmtOdds(fx.odds!.home)} / ${fmtOdds(fx.odds!.draw)} / ${fmtOdds(fx.odds!.away)}`
                : "Awaiting odds"}
            </span>
          </div>
          <div className="rnd-intel__module rnd-intel__module--ensemble">
            <span className="rnd-intel__mod-label">Ensemble</span>
            <span className={`rnd-intel__mod-val${!card?.ifpe_home_win ? " rnd-intel__mod-val--empty" : ""}`}>
              {card?.ifpe_home_win != null
                ? `H ${fmtProb(card.ifpe_home_win)} · D ${fmtProb(card.ifpe_draw)} · A ${fmtProb(card.ifpe_away_win)}`
                : "Awaiting fair-price run"}
            </span>
          </div>
        </div>
      </div>

      {/* Line Movement */}
      <div className="rnd-intel__section">
        <div className="rnd-intel__section-head">
          <IIcon name="chart" size={13} />
          <span>Line Movement</span>
        </div>
        {(data?.line_checks ?? []).length > 0 ? (
          <div className="rnd-intel__line-checks">
            {data!.line_checks.map((lc, i) => (
              <div key={i} className="rnd-intel__lc-row">
                <span className="rnd-intel__lc-type">{lc.check_type}</span>
                <span className="rnd-intel__lc-market">{lc.market}</span>
                <span>{fmtOdds(lc.previous_odds)} → {fmtOdds(lc.current_odds)}</span>
                <span className={lc.triggered_recheck ? "rnd-intel__lc-alert" : ""}>{lc.movement > 0 ? "+" : ""}{lc.movement.toFixed(2)}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="rnd-intel__empty">
            No snapshots yet. Line movement agent captures at T-72h, T-48h, T-24h, T-12h, T-6h, T-2h, T-1h.
          </div>
        )}
      </div>

      {/* Contextual Intel */}
      <div className="rnd-intel__section">
        <div className="rnd-intel__section-head">
          <IIcon name="info" size={13} />
          <span>Contextual Intel</span>
        </div>
        <div className="rnd-intel__row-pair">
          <div className="rnd-intel__context-slot">
            <span className="rnd-intel__ctx-label">Motivation</span>
            <span className={`rnd-intel__ctx-val${!card?.motivation_pressure ? " rnd-intel__ctx-val--empty" : ""}`}>
              {card?.motivation_pressure ?? "Not yet analysed"}
            </span>
          </div>
          <div className="rnd-intel__context-slot">
            <span className="rnd-intel__ctx-label">Venue Factor</span>
            <span className={`rnd-intel__ctx-val${!card?.env_advantage_score ? " rnd-intel__ctx-val--empty" : ""}`}>
              {card?.venue ?? "Not yet analysed"}
            </span>
          </div>
          <div className="rnd-intel__context-slot">
            <span className="rnd-intel__ctx-label">Squad Status</span>
            <span className={`rnd-intel__ctx-val${!card?.squad_confidence ? " rnd-intel__ctx-val--empty" : ""}`}>
              {card?.squad_confidence?.replace("_", " ") ?? "Not yet analysed"}
            </span>
          </div>
          <div className="rnd-intel__context-slot">
            <span className="rnd-intel__ctx-label">H2H Record</span>
            <span className={`rnd-intel__ctx-val${!card?.h2h_dominance ? " rnd-intel__ctx-val--empty" : ""}`}>
              {card?.h2h_dominance ?? "Not yet analysed"}
            </span>
          </div>
          <div className="rnd-intel__context-slot">
            <span className="rnd-intel__ctx-label">DNA Composite</span>
            <span className={`rnd-intel__ctx-val${card?.dna_composite == null ? " rnd-intel__ctx-val--empty" : ""}`}>
              {card?.dna_composite != null ? `${card.dna_composite.toFixed(1)} / 10` : "Not yet analysed"}
            </span>
          </div>
          <div className="rnd-intel__context-slot">
            <span className="rnd-intel__ctx-label">Referee</span>
            <span className={`rnd-intel__ctx-val${!card?.referee_profile ? " rnd-intel__ctx-val--empty" : ""}`}>
              {card?.referee_profile ?? "Not yet analysed"}
            </span>
          </div>
        </div>
      </div>

      {/* Agent pipeline status */}
      <AgentPipeline agents={agents} />

      {/* Narrative / Upset flags */}
      {(card?.narrative_flag === "MODERATE" || card?.narrative_flag === "SEVERE" || card?.upset_flag === "MEDIUM" || card?.upset_flag === "HIGH") && (
        <div className="rnd-intel__section">
          <div className="rnd-intel__section-head">
            <IIcon name="flag" size={13} />
            <span>Alerts</span>
          </div>
          <div className="rnd-intel__alerts">
            {(card.narrative_flag === "MODERATE" || card.narrative_flag === "SEVERE") && (
              <span className={`rnd-intel__alert rnd-intel__alert--narrative-${card.narrative_flag.toLowerCase()}`}>
                PUBLIC MONEY {card.narrative_flag}
              </span>
            )}
            {(card.upset_flag === "MEDIUM" || card.upset_flag === "HIGH") && (
              <span className="rnd-intel__alert rnd-intel__alert--upset">
                UPSET RISK {card.upset_flag}
              </span>
            )}
          </div>
        </div>
      )}

      {/* CLV Gate */}
      <div className="rnd-intel__section">
        <div className="rnd-intel__section-head">
          <IIcon name="medal" size={13} />
          <span>CLV Gate</span>
        </div>
        {card?.clv_gate ? (
          <div className="rnd-intel__clv-result">
            <StatusPill tone={card.clv_gate === "PASS" ? "live" : card.clv_gate === "FAIL" ? "dormant" : "calibrating"}>
              {card.clv_gate.replace("_", " ")}
            </StatusPill>
            {card.clv_gate_reason && <span className="rnd-intel__clv-reason">{card.clv_gate_reason}</span>}
          </div>
        ) : (
          <div className="rnd-intel__empty">
            CLV Gate runs at T-48h (synthesis) and T-6h (per fixture). Awaiting analysis.
          </div>
        )}
      </div>

      {legs.length > 0 && (
        <div className="rnd-intel__mods">
          <div className="rnd-intel__mods-head">Cross-product moderation</div>
          {legs.map((l, i) => (
            <div key={i} className={`rnd-intel__mod-row rnd-intel__mod-row--${l.verdict.toLowerCase()}`}>
              <span className="rnd-intel__mod-icon">{l.verdict === "APPROVED" ? "✓" : "✗"}</span>
              <span className="rnd-intel__mod-prod">{l.product}</span>
              {l.market && <span className="rnd-intel__mod-mkt">{l.market} → {l.selection}</span>}
              {l.count > 1 && <span className="rnd-intel__mod-count">×{l.count}</span>}
              {l.reasons.length > 0 && <span className="rnd-intel__mod-reason">{l.reasons.join(", ")}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// A single fixture can appear in dozens of slips from the same product (the Acca
// Ladder builds many slips that each re-use e.g. "Over 2.5" on the same match). We
// dedupe by product+market+selection+verdict and keep a count so the card shows a
// compact tally instead of N identical rows (which used to balloon a card to 40+
// rows and break the grid).
interface DedupedLeg {
  product: string; market?: string; selection?: string;
  verdict: "APPROVED" | "REJECTED"; count: number; reasons: string[];
}
function collectFixtureLegs(fixtureId: string, mods: RoundsModerationEntry[]): DedupedLeg[] {
  const map = new Map<string, DedupedLeg>();
  const push = (product: string, verdict: "APPROVED" | "REJECTED", market?: string, selection?: string, reason?: string) => {
    const key = `${product}|${verdict}|${market ?? ""}|${selection ?? ""}`;
    const ex = map.get(key);
    if (ex) { ex.count += 1; if (reason && !ex.reasons.includes(reason)) ex.reasons.push(reason); }
    else map.set(key, { product, verdict, market, selection, count: 1, reasons: reason ? [reason] : [] });
  };
  for (const m of mods) {
    for (const al of (m.approved_legs ?? [])) {
      if ((al.fixture_id ?? al.leg_ref?.fixture_id) === fixtureId)
        push(m.product, "APPROVED", al.market ?? al.leg_ref?.market, al.selection ?? al.leg_ref?.selection);
    }
    for (const rl of (m.rejected_legs ?? [])) {
      if ((rl.fixture_id ?? rl.leg_ref?.fixture_id) === fixtureId)
        push(m.product, "REJECTED", rl.market ?? rl.leg_ref?.market, rl.selection ?? rl.leg_ref?.selection, rl.reason_code);
    }
  }
  return [...map.values()].sort((a, b) =>
    a.verdict === b.verdict ? b.count - a.count : a.verdict === "APPROVED" ? -1 : 1);
}

function RoundFixtureCard({ fx, mods, isOpen, onToggle }: {
  fx: RoundFixture; mods: RoundsModerationEntry[];
  isOpen: boolean; onToggle: () => void;
}): JSX.Element {
  const legs = collectFixtureLegs(fx.fixture_id, mods);
  const approvedTotal = legs.reduce((a, l) => a + (l.verdict === "APPROVED" ? l.count : 0), 0);
  const rejectedTotal = legs.reduce((a, l) => a + (l.verdict === "REJECTED" ? l.count : 0), 0);
  const topLeg = legs.find((l) => l.verdict === "APPROVED" && l.market) ?? legs.find((l) => l.market);

  const hasOdds = fx.odds && (fx.odds.home || fx.odds.draw || fx.odds.away);
  const kickDate = new Date(fx.kickoff_at);
  const hoursAway = Math.max(0, (kickDate.getTime() - Date.now()) / 3600_000);

  return (
    <div className={`rnd-fx${fx.signals.length > 0 ? " rnd-fx--has-edge" : ""}${legs.length > 0 ? " rnd-fx--moderated" : ""}${isOpen ? " rnd-fx--open" : ""}`}>
      <div className="rnd-fx__card" onClick={onToggle} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}>
        <div className="rnd-fx__header">
          <div className="rnd-fx__kick">
            <span className="rnd-fx__kick-time">{fmtKick(fx.kickoff_at)}</span>
            <span className="rnd-fx__kick-date">{fmtDateOnly(fx.kickoff_at.slice(0, 10))}</span>
          </div>
          {hoursAway <= 72 && hoursAway > 0 && (
            <span className="rnd-fx__countdown">T-{hoursAway < 1 ? "<1h" : `${Math.floor(hoursAway)}h`}</span>
          )}
          {fx.status !== "upcoming" && fx.status !== "NS" && (
            <StatusPill tone={fx.status === "FT" ? "dormant" : "live"}>{fx.status}</StatusPill>
          )}
          <span className={`rnd-fx__chevron${isOpen ? " rnd-fx__chevron--open" : ""}`}>
            <IIcon name="chev" size={14} />
          </span>
        </div>

        <div className="rnd-fx__matchup">
          <span className="rnd-fx__team rnd-fx__team--home">
            <NationFlag code={fx.home_code} size={22} />
            {fx.home_team}
          </span>
          <span className="rnd-fx__divider">v</span>
          <span className="rnd-fx__team rnd-fx__team--away">
            <NationFlag code={fx.away_code} size={22} />
            {fx.away_team}
          </span>
        </div>

        {hasOdds ? (
          <div className="rnd-fx__odds-strip">
            <div className="rnd-fx__odds-cell"><em>H</em>{fmtOdds(fx.odds!.home)}</div>
            <div className="rnd-fx__odds-cell"><em>D</em>{fmtOdds(fx.odds!.draw)}</div>
            <div className="rnd-fx__odds-cell"><em>A</em>{fmtOdds(fx.odds!.away)}</div>
          </div>
        ) : (
          <div className="rnd-fx__odds-strip rnd-fx__odds-strip--empty">
            <span>Odds pending</span>
          </div>
        )}

        {fx.signals.length > 0 && (
          <div className="rnd-fx__signals">
            {fx.signals.map((sig, i) => {
              const isModel = sig.role === "model";        // IFPE model read (desk call pending)
              const primary = sig.role !== "secondary" && !isModel;
              const tierLabel = sig.conviction_tier === "PROBABILITY_ONLY"
                ? "Conviction"
                : sig.conviction_tier ? `Tier ${sig.conviction_tier}` : null;
              const badge = isModel ? "Model" : primary ? "Desk call" : "Conviction";
              return (
                <div key={i} className="rnd-fx__signal" style={{ flexWrap: "wrap", opacity: primary || isModel ? 1 : 0.92 }}>
                  <span style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                    padding: "1px 6px", borderRadius: 4,
                    background: isModel ? "rgba(93,154,191,0.16)" : primary ? "rgba(110,180,120,0.16)" : "rgba(150,160,200,0.14)",
                    color: isModel ? "var(--sw-blue, #5d9abf)" : primary ? "var(--sw-green, #7fd094)" : "var(--sw-text-muted, #9aa0b5)",
                  }}>{badge}</span>
                  <span className="rnd-fx__sig-market">{sig.market}</span>
                  <span className="rnd-fx__sig-sel">{sig.selection}</span>
                  <span className="rnd-fx__sig-odds">
                    {fmtOdds(sig.fair_odds)} <em>fair</em>
                    {sig.market_odds != null ? <> / {fmtOdds(sig.market_odds)} <em>book</em></> : null}
                  </span>
                  {sig.clv_gap != null && (
                    <span className={`rnd-fx__sig-clv${sig.clv_gap > 0 ? " rnd-fx__sig-clv--pos" : ""}`}>
                      {sig.clv_gap > 0 ? "+" : ""}{(sig.clv_gap * 100).toFixed(1)}%
                    </span>
                  )}
                  {tierLabel && <span className="rnd-fx__sig-tier">{tierLabel}</span>}
                  {sig.reasoning && (
                    <span style={{ flexBasis: "100%", fontSize: 11, color: "var(--sw-text-muted, #9aa0b5)", marginTop: 3, lineHeight: 1.35 }}>
                      {sig.reasoning}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {legs.length > 0 && (
          <div className="rnd-fx__mod-tally">
            {approvedTotal > 0 && (
              <span className="rnd-fx__mod-pill rnd-fx__mod-pill--approved">✓ {approvedTotal} approved</span>
            )}
            {rejectedTotal > 0 && (
              <span className="rnd-fx__mod-pill rnd-fx__mod-pill--rejected">✗ {rejectedTotal} rejected</span>
            )}
            {topLeg?.market && (
              <span className="rnd-fx__mod-top">
                {topLeg.market} → {topLeg.selection}{topLeg.count > 1 ? ` ×${topLeg.count}` : ""}
              </span>
            )}
          </div>
        )}
      </div>

      {isOpen && <IntelPanelBoundary><FixtureIntelPanel fx={fx} legs={legs} /></IntelPanelBoundary>}
    </div>
  );
}

function RoundPanel({ round, mods, isActive }: { round: RoundBucket; mods: RoundsModerationEntry[]; isActive: boolean }): JSX.Element {
  const [openFxId, setOpenFxId] = useState<string | null>(null);
  const totalSignals = round.fixtures.reduce((a, f) => a + f.signals.length, 0);
  const pricedCount = round.fixtures.filter((f) => f.odds?.home).length;
  const pricedPct = round.fixture_count > 0 ? Math.round((pricedCount / round.fixture_count) * 100) : 0;
  const roundMods = mods.filter((m) => {
    const fids = new Set(round.fixtures.map((f) => f.fixture_id));
    const hit = (legs: RoundsModerationEntry["approved_legs"] | RoundsModerationEntry["rejected_legs"]) =>
      (legs ?? []).some((l) => fids.has((l.fixture_id ?? l.leg_ref?.fixture_id) ?? ""));
    return hit(m.approved_legs) || hit(m.rejected_legs);
  });

  if (!isActive) return <></>;

  return (
    <div className="rnd-panel">
      <div className="rnd-panel__hero">
        <div className="rnd-panel__hero-left">
          <span className="rnd-panel__tournament">{round.tournament}</span>
          <h3 className="rnd-panel__title">{roundLabel(round.round_code)}</h3>
          <span className="rnd-panel__window">
            {fmtDateOnly(round.window_start)} → {fmtDateOnly(round.window_end)}
          </span>
        </div>
        <div className="rnd-panel__hero-stats">
          <div className="rnd-panel__stat">
            <span className="rnd-panel__stat-val">{round.fixture_count}</span>
            <span className="rnd-panel__stat-lbl">Fixtures</span>
          </div>
          <div className="rnd-panel__stat">
            <span className="rnd-panel__stat-val">{totalSignals}</span>
            <span className="rnd-panel__stat-lbl">Edges</span>
          </div>
          <div className="rnd-panel__stat">
            <span className="rnd-panel__stat-val">{pricedPct}%</span>
            <span className="rnd-panel__stat-lbl">Priced</span>
          </div>
          <div className="rnd-panel__stat">
            <span className="rnd-panel__stat-val">{roundMods.length}</span>
            <span className="rnd-panel__stat-lbl">Moderated</span>
          </div>
        </div>
      </div>

      {pricedCount > 0 && pricedCount < round.fixture_count && (
        <div className="rnd-panel__progress">
          <div className="rnd-panel__progress-bar">
            <div className="rnd-panel__progress-fill" style={{ width: `${pricedPct}%` } as CSSProperties} />
          </div>
          <span className="rnd-panel__progress-label">{pricedCount}/{round.fixture_count} fixtures priced</span>
        </div>
      )}

      <div className="rnd-panel__grid">
        {round.fixtures.map((fx) => (
          <RoundFixtureCard
            key={fx.fixture_id}
            fx={fx}
            mods={roundMods}
            isOpen={openFxId === fx.fixture_id}
            onToggle={() => setOpenFxId(openFxId === fx.fixture_id ? null : fx.fixture_id)}
          />
        ))}
      </div>
    </div>
  );
}

function RoundsSection(): JSX.Element {
  const { board, loading, error } = useRoundsBoard();
  const [activeIdx, setActiveIdx] = useState(0);

  if (loading) return <Loading label="Loading rounds…" />;
  if (error) return <AwaitPanel icon="info" title="Couldn't load Rounds board" body={error} />;
  if (!board || board.rounds.length === 0) {
    return (
      <AwaitPanel
        icon="medal"
        title="No rounds visible yet"
        body="Rounds derive from real persisted fixtures. The moment fixtures land in the feed for a UCL / UEL / Euro qual / WC matchday, the round shows up here automatically."
      />
    );
  }

  const totalEdges = board.rounds.reduce((a, r) => a + r.fixtures.reduce((b, f) => b + f.signals.length, 0), 0);
  const totalFixtures = board.rounds.reduce((a, r) => a + r.fixture_count, 0);
  const totalPriced = board.rounds.reduce((a, r) => a + r.fixtures.filter((f) => f.odds?.home).length, 0);

  return (
    <div className="rnd">
      <div className="rnd__overview">
        <div className="rnd__overview-stat">
          <IIcon name="flag" size={14} />
          <span>{board.rounds.length} round{board.rounds.length === 1 ? "" : "s"}</span>
        </div>
        <div className="rnd__overview-stat">
          <IIcon name="globe" size={14} />
          <span>{totalFixtures} fixture{totalFixtures === 1 ? "" : "s"}</span>
        </div>
        <div className="rnd__overview-stat">
          <IIcon name="target" size={14} />
          <span>{totalEdges} edge{totalEdges === 1 ? "" : "s"}</span>
        </div>
        <div className="rnd__overview-stat">
          <IIcon name="chart" size={14} />
          <span>{totalPriced}/{totalFixtures} priced</span>
        </div>
      </div>

      <nav className="rnd__tabs">
        {board.rounds.map((r, i) => {
          const edgeCount = r.fixtures.reduce((a, f) => a + f.signals.length, 0);
          return (
            <button
              key={`${r.tournament}|${r.round_code}`}
              className={`rnd__tab${i === activeIdx ? " rnd__tab--active" : ""}`}
              onClick={() => setActiveIdx(i)}
            >
              <span className="rnd__tab-label">{r.round_code.replace("WC-", "")}</span>
              {edgeCount > 0 && <span className="rnd__tab-badge">{edgeCount}</span>}
            </button>
          );
        })}
      </nav>

      {board.rounds.map((r, i) => (
        <RoundPanel
          key={`${r.tournament}|${r.round_code}`}
          round={r}
          mods={board.moderation}
          isActive={i === activeIdx}
        />
      ))}
    </div>
  );
}

// International Signals — live CLV-ranked edges on the international slate (WC, etc.),
// read from the get_wc2026_board RPC's signals (already complete: teams, fair vs market
// odds, CLV gap, conviction). No fabrication — no signal = no row.
// Flattened signal: one carded fixture + its chosen desk pick. Sourced from the
// Board Rounds board, which carries real FIFA nation codes (→ flag images) and the
// desk's actual signals per fixture.
// One carded fixture paired with its chosen desk pick. Keeping the whole RoundFixture
// (not a flattened copy) lets the open overlay reuse FixtureIntelPanel + the fixture's
// other signals without a second lookup.
interface SignalEntry { fx: RoundFixture; chosen: RoundSignal; }

const tierLabel = (t: string | null | undefined): string | null =>
  !t ? null : t === "PROBABILITY_ONLY" ? "Conviction" : /^[ABC]$/.test(t) ? `Tier ${t}` : t;

// Shared header block — teams + flags + pick + CLV + odds. Used by both the grid
// card and the open overlay so they read identically.
function SignalHead({ fx, chosen, flagSize = 20 }: { fx: RoundFixture; chosen: RoundSignal; flagSize?: number }): JSX.Element {
  const clv = chosen.clv_gap;
  const pos = clv != null && clv > 0;
  const pct = clv != null ? clv * 100 : null;
  const barW = clv != null ? Math.min(100, Math.max(4, Math.abs(clv) * 600)) : 0;
  const tl = tierLabel(chosen.conviction_tier);
  return (
    <>
      <div className="isig-top">
        <span className="isig-time">{fmtKick(fx.kickoff_at)}</span>
        {tl && <span className="isig-tier">{tl}</span>}
      </div>
      <div className="isig-match">
        <span className="isig-team"><NationFlag code={fx.home_code} size={flagSize} />{fx.home_team}</span>
        <span className="isig-vs">vs</span>
        <span className="isig-team"><NationFlag code={fx.away_code} size={flagSize} />{fx.away_team}</span>
      </div>
      <div className="isig-pick">
        <span className="isig-pick-sel">{chosen.selection}</span>
        <span className="isig-pick-mkt">{chosen.market}</span>
      </div>
      <div className="isig-edge">
        <div className="isig-clv-row">
          <span className={`isig-clv ${pos ? "pos" : "neg"}`}>{pct != null ? `${pct > 0 ? "+" : ""}${pct.toFixed(1)}%` : "—"}</span>
          <span className="isig-clv-label">CLV edge</span>
        </div>
        <div className={`isig-bar ${pos ? "" : "neg"}`}><span style={{ width: `${barW}%` }} /></div>
      </div>
      <div className="isig-odds">
        <span><em>fair</em>{fmtOdds(chosen.fair_odds)}</span>
        <span><em>book</em>{fmtOdds(chosen.market_odds)}</span>
      </div>
    </>
  );
}

// One signal card. Tilts toward the cursor in real time (CSS vars set on a ref —
// no re-render per mousemove), a specular light tracks the pointer, and the resting
// glow scales with edge strength via --edge. Click lifts it into the focus overlay.
function SignalCard({ entry, onOpen }: { entry: SignalEntry; onOpen: () => void }): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const clv = entry.chosen.clv_gap;
  const edge = clv != null ? Math.min(1, Math.max(0, Math.abs(clv) * 8)) : 0; // 12.5% CLV → max glow

  const onMove = (e: ReactMouseEvent<HTMLDivElement>): void => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;   // -0.5 … 0.5
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.setProperty("--ry", `${(px * 9).toFixed(2)}deg`);
    el.style.setProperty("--rx", `${(-py * 9).toFixed(2)}deg`);
    el.style.setProperty("--mx", `${((px + 0.5) * 100).toFixed(1)}%`);
    el.style.setProperty("--my", `${((py + 0.5) * 100).toFixed(1)}%`);
  };
  const onEnter = (): void => { ref.current?.style.setProperty("--lift", "-9px"); };
  const onLeave = (): void => {
    const el = ref.current; if (!el) return;
    el.style.setProperty("--rx", "0deg");
    el.style.setProperty("--ry", "0deg");
    el.style.setProperty("--lift", "0px");
  };

  return (
    <div
      ref={ref}
      className="isig-card isig-card--clickable"
      style={{ "--edge": edge } as CSSProperties}
      onMouseMove={onMove}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
    >
      <SignalHead fx={entry.fx} chosen={entry.chosen} />
      <div className="isig-more"><span>The read · conviction · deep intel</span><IIcon name="chart" size={13} /></div>
    </div>
  );
}

// Lift-into-focus overlay. Backdrop dims + blurs; the panel scales up from the grid.
// Header repeats the card; below it: the read, a conviction meter, then the full
// FixtureIntelPanel (other angles + deep fixture intel, lazy-fetched on open).
function SignalOverlay({ entry, onClose }: { entry: SignalEntry; onClose: () => void }): JSX.Element {
  const { fx, chosen } = entry;
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const rawConf = chosen.desk_confidence;
  const conf = rawConf == null ? null : rawConf <= 1 ? rawConf * 100 : rawConf;
  const confDots = conf != null ? Math.round(conf / 10) : 0;

  return (
    <div className="isig-overlay" onClick={onClose}>
      <div className="isig-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button className="isig-close" onClick={onClose} aria-label="Close">✕</button>
        <div className="isig-modal-head">
          <SignalHead fx={fx} chosen={chosen} flagSize={24} />
        </div>

        {chosen.reasoning && (
          <div className="isig-sec">
            <p className="isig-sec-lbl">The read</p>
            <p className="isig-read">{chosen.reasoning}</p>
          </div>
        )}
        {conf != null && (
          <div className="isig-sec">
            <p className="isig-sec-lbl">Desk conviction</p>
            <div className="isig-conv">
              <span className="isig-conv-dots">
                {Array.from({ length: 10 }).map((_, i) => <span key={i} className={`isig-conv-dot${i < confDots ? " on" : ""}`} />)}
              </span>
              <span className="isig-conv-num">{Math.round(conf)}</span>
            </div>
          </div>
        )}

        <div className="isig-divider" />
        <IntelPanelBoundary><FixtureIntelPanel fx={fx} /></IntelPanelBoundary>
      </div>
    </div>
  );
}

// ── The live per-fixture signals grid (the original tier). ──
function SignalsLiveView(): JSX.Element {
  const { board, loading, error } = useRoundsBoard();
  const [open, setOpen] = useState<SignalEntry | null>(null);
  if (loading) return <Loading label="Loading international signals…" />;
  if (error) return <AwaitPanel icon="info" title="Couldn't load international signals" body={error} />;
  const entries: SignalEntry[] = [];
  for (const round of board?.rounds ?? []) {
    for (const fx of round.fixtures) {
      if (!fx.signals?.length) continue;
      const primary = fx.signals.find((g) => g.role === "primary");
      const chosen = primary ?? [...fx.signals].sort((a, b) => (b.clv_gap ?? -Infinity) - (a.clv_gap ?? -Infinity))[0];
      if (!chosen) continue;
      entries.push({ fx, chosen });
    }
  }
  entries.sort((a, b) => (b.chosen.clv_gap ?? -Infinity) - (a.chosen.clv_gap ?? -Infinity));
  if (entries.length === 0) {
    return <AwaitPanel icon="target" title="No live international signals yet" body="Signals appear here once the desk cards upcoming fixtures. No card = no row." />;
  }
  return (
    <div>
      <p className="isig-count">{entries.length} live edge{entries.length === 1 ? "" : "s"} from the desk · sorted by CLV gap · click any card for the full read.</p>
      <div className="isig-grid">
        {entries.map((e, i) => <SignalCard key={`${e.fx.fixture_id}-${i}`} entry={e} onOpen={() => setOpen(e)} />)}
      </div>
      {open && <SignalOverlay entry={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

// ── Standing 10 card — the week's curated legs (rank, rationale, settled badge). ──
function StandingCard({ leg }: { leg: StandingLeg }): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const clv = leg.clv_gap;
  const pct = clv != null ? clv * 100 : null;
  const edge = clv != null ? Math.min(1, Math.max(0, Math.abs(clv) * 4)) : 0.2;
  const onMove = (e: ReactMouseEvent<HTMLDivElement>): void => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5, py = (e.clientY - r.top) / r.height - 0.5;
    el.style.setProperty("--ry", `${(px * 8).toFixed(2)}deg`); el.style.setProperty("--rx", `${(-py * 8).toFixed(2)}deg`);
    el.style.setProperty("--mx", `${((px + 0.5) * 100).toFixed(1)}%`); el.style.setProperty("--my", `${((py + 0.5) * 100).toFixed(1)}%`);
  };
  const onEnter = (): void => { ref.current?.style.setProperty("--lift", "-7px"); };
  const onLeave = (): void => {
    const el = ref.current; if (!el) return;
    el.style.setProperty("--rx", "0deg"); el.style.setProperty("--ry", "0deg"); el.style.setProperty("--lift", "0px");
  };
  const r = leg.result;
  return (
    <div ref={ref} className={`isig-card isig-standing${r ? ` settled-${r.toLowerCase()}` : ""}`} style={{ "--edge": edge } as CSSProperties}
      onMouseMove={onMove} onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <div className="isig-top">
        <span className="isig-rank">#{leg.rank}</span>
        {r && <span className={`isig-result ${r.toLowerCase()}`}>{r}{leg.score ? ` · ${leg.score}` : ""}</span>}
        {!r && <span className="isig-time">{fmtKick(leg.kickoff)}</span>}
      </div>
      <div className="isig-match">
        <span className="isig-team"><NationFlag code={leg.home_code} size={18} />{leg.fixture.split(" v ")[0]}</span>
        <span className="isig-vs">vs</span>
        <span className="isig-team"><NationFlag code={leg.away_code} size={18} />{leg.fixture.split(" v ")[1] ?? ""}</span>
      </div>
      <div className="isig-pick">
        <span className="isig-pick-sel">{leg.selection}</span>
        {leg.desk_confidence != null && <span className="isig-pick-mkt">{Math.round(leg.desk_confidence)}% model</span>}
      </div>
      {leg.rationale && <p className="isig-rationale">{leg.rationale}</p>}
      <div className="isig-odds">
        <span><em>book</em>{fmtOdds(leg.market_odds)}</span>
        {pct != null && <span><em>edge</em>{`${pct > 0 ? "+" : ""}${pct.toFixed(1)}%`}</span>}
      </div>
    </div>
  );
}

// ── The weekly curated Standing 10 tier. ──
function StandingTenView(): JSX.Element {
  const { data, loading, error } = useStandingTen();
  if (loading) return <Loading label="Loading Standing 10…" />;
  if (error) return <AwaitPanel icon="info" title="Couldn't load Standing 10" body={error} />;
  const legs = data?.legs ?? [];
  if (!data?.week_start || legs.length === 0) {
    return <AwaitPanel icon="target" title="No Standing 10 yet this week" body="The desk curates the week's 10 best every Monday. Empty by design until then." />;
  }
  const s = data.stats;
  const weekLabel = new Date(data.week_start + "T12:00:00Z").toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return (
    <div>
      <div className="isig-standing-head">
        <p className="isig-count">Week of {weekLabel} · the desk's {legs.length} standing call{legs.length === 1 ? "" : "s"} · locked for the week.</p>
        <div className="isig-standing-stats">
          {s.settled > 0 && <span className="st-stat"><b>{s.won}</b>/{s.settled} landed</span>}
          {s.hit_rate != null && <span className="st-stat">hit <b>{Math.round(s.hit_rate * 100)}%</b></span>}
          {s.roi != null && <span className={`st-stat ${s.roi >= 0 ? "pos" : "neg"}`}>ROI <b>{s.roi >= 0 ? "+" : ""}{(s.roi * 100).toFixed(0)}%</b></span>}
          {s.pending > 0 && <span className="st-stat muted">{s.pending} pending</span>}
        </div>
      </div>
      <div className="isig-grid">
        {legs.map((leg) => <StandingCard key={leg.rank} leg={leg} />)}
      </div>
    </div>
  );
}

function IntlSignalsSection(): JSX.Element {
  const [tab, setTab] = useState<"signals" | "standing">("signals");
  return (
    <div>
      <div className="isig-tabs" role="tablist">
        <button className={tab === "signals" ? "on" : ""} role="tab" aria-selected={tab === "signals"} onClick={() => setTab("signals")}>Signals</button>
        <button className={tab === "standing" ? "on" : ""} role="tab" aria-selected={tab === "standing"} onClick={() => setTab("standing")}>Standing 10</button>
      </div>
      {tab === "signals" ? <SignalsLiveView /> : <StandingTenView />}
    </div>
  );
}

// ── Page router ────────────────────────────────────────────────────────
export function InternationalPage({ section }: { section: IntlSection }): JSX.Element {
  return (
    <Shell section={section}>
      {section === "dashboard" && <DashboardSection />}
      {section === "signals" && <IntlSignalsSection />}
      {section === "wc-hub" && <WcHubSection />}
      {section === "rounds" && <RoundsSection />}
      {section === "clv" && (
        <EmptyHere
          title="No realized CLV on international picks yet"
          body="CLV accrues as picks settle. Pinnacle is the truth benchmark; Stake is the execution baseline. The chart fills automatically as international slips close."
        />
      )}
      {section === "no-bets" && (
        <EmptyHere
          title="No declined international fixtures logged yet"
          body="Every match we pass on appears here with the honest reason for skipping it. Empty until the intl publisher logs its first decline."
        />
      )}
    </Shell>
  );
}
